import React from 'react';
import {StatesButton, StatesButtonStates, ThreeDotsLoader, ButtonSize} from '@wix/wix-ui-tpa/cssVars';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../Widget/ControllerContext';
import {classes} from './PlaceOrderButton.st.css';
import {usePaymentsApi} from '../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {ICashierPaymentsApi} from '@wix/cashier-payments-widget';
import {useFunctionResultObservation} from '@wix/function-result-observation';
import {useFedopsLogger, usePanorama, useExperiments} from '@wix/yoshi-flow-editor';
import {FedopsInteractions} from '../constants';
import {isAPaymentError, PaymentError} from '../../../types/payment.types';
import {ClientErrorCode} from '../../../domain/utils/errors';
import {toMonitorError} from '../../../domain/utils/errorMonitor.utils';
import {StepId} from '../../../types/checkoutApp.types';
import {useBillingData} from '../CheckoutSteps/Steps/PaymentAndPlaceOrderStep/WithBillingData';
import {useMemberDetailsData} from '../MemberDetails/WithMemberDetailsData';
import {UsePlaceOrderButtonState} from '../CheckoutSteps/Steps/PaymentAndPlaceOrderStep/UsePlaceOrderButtonState';
import {SPECS} from '../../../common/constants';
import {PaymentCompleteResultPublic} from '@wix/cashier-payments-widget/src/sdk/types/PaymentCompleteResult';
import {PlaceOrderResponseModel} from '../../../domain/models/checkout/PlaceOrderResponse.model';
import {getBillingDetails, getMandatoryFields} from '../../../domain/utils/billingDetails.utils';

export enum PlaceOrderButtonDataHooks {
  button = 'place-order-button',
  threeDotsLoader = 'PlaceOrderButtonDataHooks.threeDotsLoader',
}

export const PlaceOrderButton = ({
  checkboxesRef,
  paymentWidgetRef,
}: {
  checkboxesRef?: React.RefObject<HTMLDivElement>;
  paymentWidgetRef?: React.RefObject<HTMLDivElement>;
}) => {
  const localeKeys = useLocaleKeys();
  const {experiments} = useExperiments();

  const {paymentsApi, activePaymentId} = usePaymentsApi();
  const {
    navigationStore: {navigateToThankYouPage, isDonationsTheme, navigateToExternalUrl},
    checkoutStore,
    paymentStore,
    checkboxesStore: {areCheckoutCheckboxesValid, isSubscriptionCheckboxChecked, setWasFormSubmitted},
    stepsManagerStore: {activeStep},
    checkoutSettingsStore: {checkoutContent},
  } = useControllerProps();

  const {
    isPlaceOrderButtonDisabled,
    cartSummary,
    cart,
    isFastFlow,
    setPlaceOrderPaymentError,
    isShippingFlow,
    onInvalidDetailsFormSubmit,
  } = checkoutStore;

  const {withObservation} = useFunctionResultObservation();
  const clickPlaceOrderButton = withObservation(checkoutStore, 'clickPlaceOrderButton');
  const setPaymentAndBillingDetails = withObservation(checkoutStore, 'setPaymentAndBillingDetails');
  const confirmPayment = withObservation(paymentStore, 'confirmPayment');
  const fedops = useFedopsLogger();
  const panorama = usePanorama();

  const makeGetPaymentDetailsIdSync = experiments.enabled(SPECS.MakeGetPaymentDetailsIdSync);
  const callShowPaymentErrorModalAfterValidation = experiments.enabled(SPECS.CallShowPaymentErrorModalAfterValidation);

  const {isFormValid, billingSameAsShipping, isShippingValidForBilling, getBillingFormDataForSubmit} = useBillingData();

  const {selectedAddressesServiceId, selectedAddressesService} = useMemberDetailsData();

  const {
    placeOrderButtonState,
    shouldPlaceOrderButtonBeDisabled,
    setPlaceOrderButtonState,
    setShouldPlaceOrderButtonBeDisabled,
  } = UsePlaceOrderButtonState();

  const isOnPaymentAndPlaceOrderStep = activeStep.stepId === StepId.paymentAndPlaceOrder;

  const scrollToRef = (ref?: React.RefObject<HTMLDivElement>) => {
    ref?.current?.scrollIntoView({behavior: 'smooth'});
  };

  const shouldRequirePayment = cartSummary!.paymentSummary.requiresPaymentAfterGiftCard;

  const resetPlaceOrderButtonState = ({shouldReportErrorInCheckoutBI}: {shouldReportErrorInCheckoutBI: boolean}) => {
    /* istanbul ignore else */
    if (shouldReportErrorInCheckoutBI) {
      onInvalidDetailsFormSubmit();
    }
    setPlaceOrderButtonState(StatesButtonStates.IDLE);
    setShouldPlaceOrderButtonBeDisabled(isPlaceOrderButtonDisabled);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleSubmit = async () => {
    if (!areCheckoutCheckboxesValid) {
      setWasFormSubmitted(true);
      scrollToRef(checkboxesRef);
      return;
    }

    setPlaceOrderButtonState(StatesButtonStates.IN_PROGRESS);
    setShouldPlaceOrderButtonBeDisabled(true);

    let price!: number;
    let detailsId;

    const setBillingSameAsShipping = isShippingFlow && billingSameAsShipping && isShippingValidForBilling;
    const billingFormDataForSubmit = getBillingFormDataForSubmit();
    const email = billingFormDataForSubmit?.email;
    const billingDetails = getBillingDetails({
      billingAddress: billingFormDataForSubmit?.billingAddress,
      billingContact: billingFormDataForSubmit?.billingContact ?? undefined,
      cart,
      setBillingSameAsShipping,
      activePaymentId,
      selectedAddressesService,
      selectedAddressesServiceId,
    });

    if (makeGetPaymentDetailsIdSync) {
      if (shouldRequirePayment) {
        const mandatoryFields = getMandatoryFields(
          billingDetails?.billingAddress,
          billingDetails?.billingContact,
          email || cart.customerInfo.email
        );

        await paymentsApi?.setMandatoryFields?.(mandatoryFields);
      }

      price = cartSummary!.paymentSummary.payNow.amount;
      detailsId = await getPaymentsDetailsId({
        paymentsApi,
        price,
        fedops,
        panorama,
        setPlaceOrderPaymentError,
        isCardTokenizationCheckout: cart.isCardTokenizationCheckout,
        shouldRequirePayment,
        callShowPaymentErrorModalAfterValidation,
      });
    }

    if (isOnPaymentAndPlaceOrderStep && shouldRequirePayment) {
      const isBillingFormValid = await isFormValid();
      fedops.interactionStarted(FedopsInteractions.ValidatePaymentInteraction);
      panorama.transaction(FedopsInteractions.ValidatePaymentInteraction).start();
      const {isValid: isPaymentValid} = (await paymentsApi?.validate()) ?? {};
      fedops.interactionEnded(FedopsInteractions.ValidatePaymentInteraction);
      panorama.transaction(FedopsInteractions.ValidatePaymentInteraction).finish();

      if (!isPaymentValid) {
        resetPlaceOrderButtonState({shouldReportErrorInCheckoutBI: true});
        scrollToRef(paymentWidgetRef);
        return;
      }

      if (!isBillingFormValid) {
        resetPlaceOrderButtonState({shouldReportErrorInCheckoutBI: true});
        return;
      }

      let hasUpdateSucceeded = false;

      hasUpdateSucceeded = await setPaymentAndBillingDetails({
        ...billingDetails,
        email,
        activePaymentId,
      });

      if (!hasUpdateSucceeded) {
        return;
      }
    }

    if (!makeGetPaymentDetailsIdSync) {
      price = cartSummary!.paymentSummary.payNow.amount;
      detailsId = await getPaymentsDetailsId({
        paymentsApi,
        price,
        fedops,
        panorama,
        setPlaceOrderPaymentError,
        isCardTokenizationCheckout: cart.isCardTokenizationCheckout,
        shouldRequirePayment,
        callShowPaymentErrorModalAfterValidation: false,
      });
    }

    if (isAPaymentError(detailsId)) {
      // istanbul ignore else
      if (callShowPaymentErrorModalAfterValidation) {
        setPlaceOrderPaymentError(detailsId as PaymentError, ClientErrorCode.CLIENT_PAYMENT_ERROR);
      }
      return;
    }
    const paymentResponse: PlaceOrderResponseModel | undefined = await clickPlaceOrderButton(
      isSubscriptionCheckboxChecked,
      detailsId as string | undefined
    );

    await handlePaymentResponse(paymentResponse, price, cart.isCardTokenizationCheckout);
  };

  const shouldNavigateToExternalUrl = ({
    paymentResponse,
    continuePaymentResponse,
  }: {
    paymentResponse: PlaceOrderResponseModel;
    continuePaymentResponse: PaymentCompleteResultPublic;
  }) => {
    return !paymentResponse.completed && continuePaymentResponse?.redirectUrl;
  };

  const navigateToTYPOrRedirectUrl = ({
    paymentResponse,
    continuePaymentResponse,
  }: {
    paymentResponse: PlaceOrderResponseModel;
    continuePaymentResponse?: PaymentCompleteResultPublic;
  }) => {
    if (continuePaymentResponse && shouldNavigateToExternalUrl({paymentResponse, continuePaymentResponse})) {
      void navigateToExternalUrl(continuePaymentResponse.redirectUrl!);
      return;
    }
    void navigateToThankYouPage({
      orderId: paymentResponse.orderId,
    });
  };

  const handlePaymentResponse = async (
    paymentResponse: PlaceOrderResponseModel | undefined,
    price: number,
    isCardTokenizationCheckout?: boolean
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    if (paymentResponse?.paymentError?.failureDetails) {
      const errorMessage =
        /* istanbul ignore next */ (await paymentsApi?.localizeError?.(
          paymentResponse?.paymentError?.failureDetails
        )) ?? localeKeys.checkout.page.generalPaymentError.subtitle();
      setPlaceOrderPaymentError(
        {
          translatedError: errorMessage,
          status: paymentResponse.paymentError.status,
          failureDetails: paymentResponse?.paymentError?.failureDetails,
        } as PaymentError,
        ClientErrorCode.CLIENT_PAYMENT_ERROR
      );
      return;
    }
    let continuePaymentResponse;
    if (
      paymentsApi &&
      paymentResponse?.paymentResponseToken &&
      (price > 0 || isCardTokenizationCheckout || shouldRequirePayment)
    ) {
      try {
        continuePaymentResponse = await paymentsApi.continuePayment?.(paymentResponse.paymentResponseToken);
      } catch (e: unknown) {
        /* istanbul ignore next */
        panorama.errorMonitor().reportError(...toMonitorError(e, 'PlaceOrderButton continuePayment'));
        /* istanbul ignore next */
        const paymentError = e as PaymentError;
        /* istanbul ignore next */
        if (paymentError.status === 'BUYER_CANCELED') {
          resetPlaceOrderButtonState({shouldReportErrorInCheckoutBI: false});
        } else {
          /* istanbul ignore next */
          setPlaceOrderPaymentError(paymentError, ClientErrorCode.CLIENT_PAYMENT_ERROR);
        }
        /* istanbul ignore next */
        return;
      }
    }
    if (paymentResponse?.orderId) {
      if (isFastFlow && paymentResponse.paymentResponseToken) {
        const usePaymentStatusV2 = experiments.enabled(SPECS.UsePaymentStatusV2);
        // https://github.com/wix-private/cashier-client/blob/master/packages/express-checkout-widget/docs/Payment%20confirmation.md#payment-confirmation
        const result = await confirmPayment({
          chargeResponseToken: paymentResponse.paymentResponseToken,
          ...(!usePaymentStatusV2 && {newApi: true}),
        });
        /* istanbul ignore next */
        const shouldContinuePayment = usePaymentStatusV2
          ? result.clientStatusV2 !== 'InProcess'
          : result.clientStatus !== 'Pending';
        /* istanbul ignore next */
        if (shouldContinuePayment) {
          navigateToTYPOrRedirectUrl({paymentResponse, continuePaymentResponse});
        }
        /* istanbul ignore next */
        return;
        /* istanbul ignore next */
      }
      navigateToTYPOrRedirectUrl({paymentResponse, continuePaymentResponse});
    }
  };

  const getPlaceOrderButtonIdleContent = (): string => {
    let placeOrderButtonIdleContent: string = '';
    if (cartSummary!.paymentSummary.payNow.amount > 0) {
      const placeOrderAndPayText = isDonationsTheme
        ? localeKeys.checkout.donations.donateNow.button()
        : localeKeys.checkout.placeOrderPay.button();
      placeOrderButtonIdleContent = checkoutContent?.placeOrderPayButton ?? placeOrderAndPayText;
    } else {
      const placeOrderText = localeKeys.checkout.place_order.place_order_button();
      placeOrderButtonIdleContent = checkoutContent?.placeOrderButton ?? placeOrderText;
    }

    return placeOrderButtonIdleContent;
  };

  function getPlaceOrderButton() {
    return (
      <StatesButton
        className={classes.placeOrderButton}
        data-hook={PlaceOrderButtonDataHooks.button}
        disabled={shouldPlaceOrderButtonBeDisabled}
        upgrade={true}
        size={ButtonSize.medium}
        onClick={() => {
          void handleSubmit();
        }}
        idleContent={getPlaceOrderButtonIdleContent()}
        state={placeOrderButtonState}
        inProgressContent={
          <ThreeDotsLoader data-hook={PlaceOrderButtonDataHooks.threeDotsLoader} className={classes.threeDotButton} />
        }
        wrapContent
      />
    );
  }
  return getPlaceOrderButton();
};

async function getPaymentsDetailsId({
  paymentsApi,
  price,
  fedops,
  panorama,
  setPlaceOrderPaymentError,
  isCardTokenizationCheckout,
  shouldRequirePayment,
  callShowPaymentErrorModalAfterValidation,
}: {
  paymentsApi?: ICashierPaymentsApi;
  price: number;
  fedops: ReturnType<typeof useFedopsLogger>;
  panorama: ReturnType<typeof usePanorama>;
  setPlaceOrderPaymentError: (paymentError: PaymentError, errorCode: string) => void;
  isCardTokenizationCheckout?: boolean;
  shouldRequirePayment: boolean;
  callShowPaymentErrorModalAfterValidation: boolean;
}): Promise<string | undefined | PaymentError> {
  if (!paymentsApi || (price === 0 && !isCardTokenizationCheckout && !shouldRequirePayment)) {
    return;
  }
  fedops.interactionStarted(FedopsInteractions.InitializePaymentInPlaceOrderInteraction);
  panorama.transaction(FedopsInteractions.InitializePaymentInPlaceOrderInteraction).start();
  try {
    const initializeResponse = await paymentsApi?.initializePayment?.();
    fedops.interactionEnded(FedopsInteractions.InitializePaymentInPlaceOrderInteraction);
    panorama.transaction(FedopsInteractions.InitializePaymentInPlaceOrderInteraction).finish();
    return initializeResponse?.detailsId;
  } catch (e: unknown) {
    panorama.errorMonitor().reportError(...toMonitorError(e, 'PlaceOrderButton getPaymentsDetailsId'));
    const paymentError = e as PaymentError;
    if (!callShowPaymentErrorModalAfterValidation) {
      setPlaceOrderPaymentError(paymentError, ClientErrorCode.CLIENT_PAYMENT_ERROR);
    }

    return paymentError;
  }
}
